import type { AxiosInstance } from 'axios';

export interface CsvColumn<T> {
  label: string;
  value: (row: T) => unknown;
}

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/["\n,]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const header = columns.map((c) => csvEscape(c.label)).join(',');
  const lines = rows.map((row) => columns.map((c) => csvEscape(c.value(row))).join(','));
  return [header, ...lines].join('\r\n');
}

export function downloadCsvText(filename: string, csvText: string): void {
  // Leading BOM so Excel opens the file as UTF-8 instead of guessing wrong.
  const blob = new Blob(['﻿' + csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

interface LaravelPageShape<T> {
  data: T[];
  last_page?: number;
}

// Fetches every page of a paginated Laravel endpoint using its existing filters.
export async function fetchAllPages<T>(
  api: AxiosInstance,
  url: string,
  params: Record<string, unknown> = {}
): Promise<T[]> {
  const perPage = 200;
  const first = await api.get<LaravelPageShape<T>>(url, { params: { ...params, page: 1, per_page: perPage } });
  let rows = first.data.data;
  const lastPage = first.data.last_page ?? 1;
  for (let page = 2; page <= lastPage; page++) {
    const res = await api.get<LaravelPageShape<T>>(url, { params: { ...params, page, per_page: perPage } });
    rows = rows.concat(res.data.data);
  }
  return rows;
}

export async function downloadListCsv<T>(
  api: AxiosInstance,
  url: string,
  params: Record<string, unknown>,
  columns: CsvColumn<T>[],
  filename: string
): Promise<void> {
  const rows = await fetchAllPages<T>(api, url, params);
  downloadCsvText(filename, toCsv(rows, columns));
}
